import { Heart, X, Circle, MessageSquare } from 'lucide-react'
import { motion } from 'framer-motion'
import type { Bucket } from '../types'

interface BucketBarProps {
  current?: Bucket
  onPick: (bucket: Bucket) => void
  onNote: () => void
  hasNote?: boolean
}

export function BucketBar({ current, onPick, onNote, hasNote }: BucketBarProps) {
  return (
    // The note pill used to hang at `-top-12`, OUTSIDE this bar, which put it
    // on top of whichever sibling happened to be above: the first-frame hint
    // (it covered its left third) and then the identity prompt's Save row.
    // Fixing that per-neighbour is whack-a-mole; the bar now reserves the
    // pill's height in its OWN padding and positions it inside, so it cannot
    // reach anything it does not own.
    <div className="relative flex flex-shrink-0 items-center justify-center gap-3.5 px-6 pb-7 pt-14">
      <button
        onClick={onNote}
        aria-label={hasNote ? 'Edit note' : 'Add note'}
        className={[
          'absolute left-6 top-2 h-9 px-3.5 rounded-full text-xs font-medium border backdrop-blur',
          'flex items-center gap-1.5 transition-colors',
          hasNote
            ? 'bg-[var(--accent-primary)] text-[#0a0a0a] border-[var(--accent-primary)]'
            : 'bg-[rgba(15,15,15,0.85)] text-[var(--text-primary)] border-[var(--border-default)]',
        ].join(' ')}
      >
        <MessageSquare size={13} strokeWidth={2} />
        {hasNote ? 'Note added' : 'Add note'}
      </button>
      <BucketButton
        active={current === 'skip'}
        onClick={() => onPick('skip')}
        ariaLabel="Reject"
        kind="skip"
      >
        <X size={22} strokeWidth={2.4} />
      </BucketButton>
      <BucketButton
        active={current === 'maybe'}
        onClick={() => onPick('maybe')}
        ariaLabel="Undecided"
        kind="maybe"
      >
        <Circle size={22} strokeWidth={2.2} />
      </BucketButton>
      <BucketButton
        active={current === 'love'}
        onClick={() => onPick('love')}
        ariaLabel="Select"
        kind="love"
      >
        <Heart size={28} fill="currentColor" strokeWidth={0} />
      </BucketButton>
    </div>
  )
}

interface BucketButtonProps {
  active: boolean
  onClick: () => void
  ariaLabel: string
  kind: 'skip' | 'maybe' | 'love'
  children: React.ReactNode
}

function BucketButton({ active, onClick, ariaLabel, kind, children }: BucketButtonProps) {
  const base = 'rounded-full grid place-items-center border transition-all duration-150'
  const sizes = kind === 'love' ? 'w-16 h-16' : 'w-14 h-14'
  const loveGradient =
    'bg-gradient-to-b from-[#2ee06a] to-[var(--accent-success)] border-[var(--accent-success)] text-[#052e10] shadow-glow-success-lg'
  // Select has to READ as unselected until it is selected. It used to paint the
  // full green gradient either way, with a 5% scale the only difference, so the
  // primary action gave the least feedback that it had been pressed — and every
  // frame looked already chosen. Unselected now matches the other two buttons'
  // resting shape (soft fill, ringed border, coloured glyph); selected keeps the
  // gradient and the glow it always had.
  const loveResting =
    'bg-[var(--accent-success-soft,rgba(34,197,94,.18))] border-[var(--accent-success-ring,rgba(34,197,94,.45))] text-[var(--accent-success)]'
  const styles =
    kind === 'love'
      ? active
        ? `${loveGradient} scale-105`
        : loveResting
      : kind === 'skip'
        ? active
          ? 'bg-[var(--accent-danger)] border-[var(--accent-danger)] text-white'
          : 'bg-[var(--accent-danger-soft)] border-[var(--accent-danger-ring)] text-[var(--accent-danger)]'
        : active
          ? 'bg-[var(--surface-quaternary)] border-[var(--border-strong)] text-[var(--text-primary)]'
          : 'bg-[var(--surface-secondary)] border-[var(--border-default)] text-[var(--text-primary)]'

  return (
    <motion.button
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
      className={`${base} ${sizes} ${styles}`}
      whileTap={{ scale: 0.92 }}
    >
      {children}
    </motion.button>
  )
}
